import React, { useEffect, useState } from "react";

import { Flex } from "@components/common/ResponsiveBox";
import { Text } from "@components/common/Text";
import { Spinner } from "@components/common/Spinner";
import Popover from "@components/common/Popover";

import { MarginProps } from "@styles/helpers";
import Icon from "@icons/Icon";
import { styled } from "@styles/theme";

export interface FileStatus {
  filename: string;
  file: string;
  loading: boolean;
  error?: string;
}

interface Props extends MarginProps {
  onChange: React.Dispatch<React.SetStateAction<FileStatus[]>>;
  validate?: (file: File) => string | undefined;
  accept?: string;
}

const FileButton = styled.label`
  display: flex;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;

  padding: ${(p) => p.theme.spacing.xs} ${(p) => p.theme.spacing.md};
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => p.theme.colors.white[0]};
  color: ${(p) => p.theme.colors.blue[600]};
  box-shadow: ${(p) => p.theme.shadow.xs};
`;

const RemoveButton = styled.button`
  display: flex;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const FileInput: React.FC<Props> = ({
  onChange,
  validate,
  accept = "image/x-png,image/jpeg,image/png,image/jpg",
  ...props
}) => {
  const [filesStatus, setFilesStatus] = useState<FileStatus[]>([]);

  const convertToBase64 = (selectedFile: File, filename: string) => {
    const reader = new FileReader();

    reader.readAsDataURL(selectedFile);

    reader.onload = () => {
      setFilesStatus((prevFilesStatus) => {
        return prevFilesStatus.map((fileStatus) => {
          return fileStatus.filename === filename
            ? {
                ...fileStatus,
                file: reader.result ? (reader.result as string) : "",
                loading: false,
              }
            : fileStatus;
        });
      });

      reader.onerror = () => {
        setFilesStatus((prevFilesStatus) =>
          prevFilesStatus.map((fileStatus) => {
            return fileStatus.filename === filename
              ? {
                  ...fileStatus,
                  loading: false,
                  error: "Error reading file",
                }
              : fileStatus;
          }),
        );
      };
    };

    return "";
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const newFileStatuses = newFiles.map((file) => {
      return {
        file: convertToBase64(file, file.name),
        filename: file.name,
        loading: true,
        error: validate ? validate(file) : undefined,
      };
    });

    setFilesStatus((prevFilesStatus) => [
      ...prevFilesStatus,
      ...newFileStatuses,
    ]);
  };

  const handleDelete = (fileToDelete: string) => {
    setFilesStatus((prevFilesStatus) =>
      prevFilesStatus.filter(
        (fileStatus) => fileStatus.filename !== fileToDelete,
      ),
    );
  };

  useEffect(() => {
    onChange(filesStatus);
  }, [filesStatus]);

  return (
    <Flex {...props}>
      <FileButton htmlFor="file">
        <Flex direction="row" gapX="xs" alignItems="center">
          <Icon name="copy" fill="blue.600" />
          Send file
        </Flex>
      </FileButton>

      <input
        type="file"
        id="file"
        onChange={handleOnChange}
        hidden
        multiple
        accept={accept}
      />

      <Text
        mT="md"
        mB="md"
        paragraph
        size={12}
        colorWeight={200}
        textAlign="left"
      >
        Maximum file size of 5 MB
      </Text>

      <Flex gapY="md" width="100%">
        {filesStatus.map((fileStatus, index) => (
          <Flex
            key={index}
            direction="row"
            gapX="sm"
            alignItems="center"
            justify="flex-start"
          >
            {fileStatus.error && (
              <Popover
                triggerOnHover
                tooltip="small"
                direction="top"
                content={{
                  component: fileStatus.error,
                }}
              >
                <Icon name="alert" fill="red.500" size={16} />
              </Popover>
            )}

            {fileStatus.loading && <Spinner size={16} />}

            <Text
              paragraph
              size={14}
              weight={700}
              color={fileStatus.error ? "red" : "primary"}
              colorWeight={
                fileStatus.error ? 600 : fileStatus.loading ? 0 : 300
              }
            >
              {fileStatus.filename}
            </Text>

            <RemoveButton onClick={() => handleDelete(fileStatus.filename)}>
              <Icon
                name="close"
                fill={
                  fileStatus.error
                    ? "red.600"
                    : fileStatus.loading
                    ? "primary.0"
                    : "primary.300"
                }
                size={16}
              />
            </RemoveButton>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export default FileInput;
